import axios, { type CustomParamsSerializer, type ParamsSerializerOptions } from 'axios'
import { defineStore } from 'pinia'
import hash from 'stable-hash'
import type { Split } from 'type-fest'
import { computed, ref, watch, type Ref, type ComputedRef } from 'vue'

import useLogger from '~/composables/useLogger'
import type { paths } from '~/generated/types'
import type { Album, Track } from '~/types'
import { isRateLimiterError, useRateLimiterStore } from './rateLimiter'
import { compact, memoize } from 'lodash-es'

const logger = useLogger()

/**
 * @param item
 * @returns the most unique identifier available per item, assuming every item has at least one key field
 */
export const getKey = (item: { fid: string } | { artist: { fid: string } } | { id: number } | { name: string }) =>
  'fid' in item ? item.fid : 'artist' in item ? item.artist.fid : 'id' in item ? item.id.toString() : item.name

// ======================================================================
// Object names
const _names = ['artists', 'albums', 'playlists', 'tracks', 'channels', 'radios/radios', 'tags', 'favorites/tracks', 'history/listenings'] as const satisfies (
  | Exclude<Split<keyof paths, '/'>[3], 'search'>
  | 'radios/radios'
  | 'favorites/tracks'
  | 'history/listenings'
)[]
type Name = (typeof _names)[number]

// Paginated lists
type PathMany<N extends Name> = `/api/v2/${N}/` // Has trailing slash in API
type GetPaginatedResponses<N extends Name> = paths[PathMany<N>]['get']['responses'][200]['content']['application/json']
type Params<N extends Name> = paths[PathMany<N>]['get']['parameters']['query']

// Single items by key
type KeyType<N extends Name> = {
  'artists': 'artists/{id}'
  'albums': 'albums/{id}'
  'playlists': 'playlists/{uuid}'
  'tracks': 'tracks/{id}'
  'channels': 'channels/{composite}'
  'radios/radios': 'radios/radios/{id}'
  'tags': 'tags/{name}'
  'favorites/tracks': 'favorites/tracks/{id}'
  'history/listenings': 'history/listenings/{id}'
}[N]

type PathFirst<N extends Name> = `/api/v2/${KeyType<N>}/` // Has trailing slash in API
type GetFirstResponse<N extends Name> = paths[PathFirst<N>]['get'] extends {
  responses: {
    200: {
      content: {
        'application/json': infer TData
      }
    }
  }
}
  ? TData
  : never

// ======================================================================
// Remote data
type Metadata = {
  name: Name
  key: string
  lastRequested?: number
  deduplicationKey?: string | unknown
  paramsHash: string
}
type Data<T> = (
  | {
    status: 'notAsked'
    error?: null
    data?: T
  }
  | {
    status: 'queued'
    error?: null
    data?: T
    lastUpdated: number
  }
  | { status: 'loading'
    error?: null
    data?: T
    lastUpdated: number }
    | { status: 'success'
      error?: null
      data: T
      lastUpdated: number }
      | { status: 'error'
        error: Error
        data?: T
        lastUpdated: number
      }
  ) & Metadata

const notAsked = (meta: Metadata) => ({ status: 'notAsked', ...meta }) as const

const setPending = <T>(remoteData: Data<T>): Data<T> & { status: 'queued' } =>
  ({
    ...remoteData,
    status: 'queued',
    error: null,
    lastUpdated: Date.now(),
    // TODO: consider using the rate-limiter-generated timestamp in order to remove two redundant Date.now() calculations.
    lastRequested: Date.now()
  }) as const

const setLoading = <T>(remoteData: Data<T> & { status: 'queued' | 'success' | 'error' }): Data<T> & { status: 'loading' } =>
  ({
    ...remoteData,
    status: 'loading',
    error: null,
    lastUpdated: Date.now()
  }) as const

const setSuccess = <T>(remoteData: Data<T> & { status: 'loading' }, data: T): Data<T> & { status: 'success' } =>
  ({
    ...remoteData,
    status: 'success',
    data,
    error: null,
    lastUpdated: Date.now()
  }) as const

const setError = <T>(remoteData: Data<T> & { status: 'loading' }, error: Error): Data<T> & { status: 'error' } =>
  ({
    ...remoteData,
    status: 'error',
    error,
    lastUpdated: Date.now()
  }) as const

export const invalidate = (remoteData: Data<unknown>) => {
  if ('lastUpdated' in remoteData) remoteData.lastUpdated = 0
}

// ======================================================================
// Search results

type Key = string
type SearchCategory = string

/**
 * **Fetching individual items by key**
 * - Prevent duplicate fetches: Caches the result for 1 second to prevent over-fetching and request duplication (override with { immediate: true})
 * - Share reactive objects across components: Avoid data duplication, and auto-update the Ui whenever an updated version of the data is re-fetched
 *
 * ```ts
 * import { useDataStore } from '~/ui/stores/data'
 *
 * const data = useDataStore()
 *
 * const artist15 = data.get("artist", "15") // Ref<Artist | undefined>
 * const album23 = data.get("album", "23") // Ref<Album | undefined>
 * ```
 *
 * **Fetching paginated search results**
 * - Rate limited (currently 2 requests per seconds)
 *
 * ```ts
 * const albums = data.albums({ query: 'xyz' }) // Ref<{ status, data?, error? }>
 *
 * if (albums.value.status === 'error') albums.refetch();
 *````
 *
 * As soon as data arrives, all references to the same object in all components using this store will be updated.
 *
 * Note: Pinia does not support destructuring.
 * Do not write `{ get } = useDataStore()`
 */
export const useDataStore = defineStore('data', () => {
  /**
   * Global rate limiting queue for any Api call.
   * Tasks are deduplicated using either exact request identity (for use in widgets) or search categories.
   */
  const rateLimiter = useRateLimiterStore()

  // Type map that associates cache keys with their corresponding types
  // TODO: Refactor cache (see queryMany/searches)
  type ItemType = {
    artist: GetFirstResponse<'artists'>
    album: Album
    track: Track
    // Add new types here (channel: Channel...)
  }

  type Items<I extends keyof ItemType> = Record<
    number | string, {
      result: Ref<ItemType[I] | undefined>
      timestamp: number
    }
  >

  const cache: { [I in keyof ItemType]: Items<I> } = {
    artist: {},
    album: {},
    track: {}
  }

  // ------------- Query one --------------- //
  // TODO: Errors and Loading states (`undefined` can mean an error occurred, or the request is still queued). For now, the functionality is sufficient for our UI.
  // TODO: Add rate limiting. Perhaps we can reuse the function `createResource` for the purpose of rate limiting and a proper RemoteData state machine.

  /** Inspect the cache with the Vue Devtools (Pinia tab); read-only */
  const data = computed(() => cache)

  /**
   * @param type - either 'artist' or 'album' etc.
   * @param key - The id, fid, mbid, or uuid of the item to fetch
   * @param options
   * @returns an auto-updating reference to `undefined` if there is an error or the item is not yet loaded, or the actual item once it is loaded.
   *
   * Tip: Re-run after 1 second to refresh the data.
   */
  const get = <I extends keyof ItemType>(type: I, key: number | string, options?: {
    immediate?: boolean
  }) => {
    const items = cache[type] as Items<I>

    if (!items[key]) items[key] = { result: ref<ItemType[I] | undefined>(), timestamp: 0 } // born in 1970

    if (options?.immediate || items[key].timestamp < Date.now() - 2000) {
      items[key]!.timestamp = Date.now()

      axios.get<ItemType[I]>(`${type}s/${key}/`, { params: { refresh: 'true' } })
        .then(({ data }) => {
          items[key]!.result.value = data
          items[key]!.timestamp = Date.now()
        })
        .catch((error) => {
          items[key]!.timestamp = 0
          useLogger().error(`Failed to fetch ${type} ${key}`, error)
        })
    }

    return items[key].result
  }

  const compactMany = <T>(items: ComputedRef<(T | undefined)[]>) => computed(() =>
    compact(items.value)
  )

  /**
   * Resolves a reactive array of keys into a computed array of cached items.
   *
   * @param type - The domain entity type (e.g., 'artist', 'album')
   * @param getKeys - A getter function returning the list of ids. Evaluated reactively.
   * @param options - Fetch options
   */
  const getMany = <I extends keyof ItemType>(
    type: I,
    getKeys: () => readonly (number | string)[],
    options?: Parameters<typeof get<I>>[2]
  ) =>
    compactMany(computed(() => getKeys().map(key => get(type, key, options).value)))

  // ------------- Query many --------------- //

  // Initialize items cache
  const searches = ref({
    'artists': new Map<Key, Data<GetPaginatedResponses<'artists'>>>(),
    'albums': new Map<Key, Data<GetPaginatedResponses<'albums'>>>(),
    'playlists': new Map<Key, Data<GetPaginatedResponses<'playlists'>>>(),
    'tracks': new Map<Key, Data<GetPaginatedResponses<'tracks'>>>(),
    'channels': new Map<Key, Data<GetPaginatedResponses<'channels'>>>(),
    'radios/radios': new Map<Key, Data<GetPaginatedResponses<'radios/radios'>>>(),
    'tags': new Map<Key, Data<GetPaginatedResponses<'tags'>>>(),
    'favorites/tracks': new Map<Key, Data<GetPaginatedResponses<'favorites/tracks'>>>(),
    'history/listenings': new Map<Key, Data<GetPaginatedResponses<'history/listenings'>>>()
  } as const)

  const config = ref<{
    maxAge: number
    paramsSerializer: ParamsSerializerOptions | CustomParamsSerializer
  }>({
    /** Invalidate searches every 5 minutes */
    maxAge: 5 * 60000,

    /** Use no bracket when serializing arrays such as `?tags=a&tags=b` instead of `?tags=[a,b]` or `?tags[]=a&tags[]=b` or `?tags[0]=a&tags[1]=b` */
    paramsSerializer: { indexes: null }
  })

  /**
   * @param name - the name of the resource, e.g. 'artists' or 'albums'
   */
  const createResource = <N extends Name>(name: N) =>
    /**
     * Reactive, cached resource for a given name and params. Use in `<template>` or a `<script setup>` block.
     * @param params - the query parameters to fetch the resource with (see API schema)
     * @param root0
     * @returns:
     * - Remote Data object with status, data, error fields
     * - `key` field that represents a unique hash of the current data (to govern UI re-renders)
     * - `refetch` method to manually re-fetch the data (skipping the rate limiter queue)
     *
     * Note that if you construct a resource that has been fetched more than 5 minutes ago, a new fetch job is auto-scheduled.
     * Otherwise, the cached ref is returned and no autofetch is performed.
     */
    (params: Params<N>,
      { refetchSignal, deduplicationKey, cacheKey }: {
        /**
         * Reactive signal (ref, computed, or getter) that will trigger a refetch when it changes.
         * Use lazy function (getter) for store listeners: `refetchSignal: () => store.state.moderation.lastUpdate`
         */
        refetchSignal?: Parameters<typeof watch>[0]
        /**
         * String or array that will be used as the key for deduplicating and debouncing requests in the rate limiter.
         * By default, it's the tuple of name and params.
         */
        deduplicationKey?: SearchCategory
        /**
         * Retain stale data (prevents layout shifts)
         * @see https://web.dev/articles/stale-while-revalidate
         */
        cacheKey?: string
      } = {}
    ) => {
      const key = cacheKey ?? hash(params)
      const cached = computed<Data<GetPaginatedResponses<N>>>({
        get: () => searches.value[name].get(key) ?? notAsked({ name, key, deduplicationKey, paramsHash: hash(params) }),
        set: value => (searches.value[name] as Map<Key, Data<GetPaginatedResponses<N>>>).set(key, value)
      })

      const scheduleFetch = (rateLimiterConfig?: Parameters<typeof rateLimiter.greenlight>[1]) => async () => {
        // TODO: Remove type acrobatics for request state transitions
        try {
          cached.value = setPending({ ...cached.value, paramsHash: hash(params) })
          await rateLimiter.greenlight<SearchCategory | [Name, Params<Name>]>(
            deduplicationKey || [name, params],
            rateLimiterConfig
          )
          cached.value = setLoading(cached.value as Data<GetPaginatedResponses<N>> & { status: 'queued' })
          const { data } = await axios.get<GetPaginatedResponses<N>>(name, { params, paramsSerializer: config.value.paramsSerializer })
          cached.value = setSuccess(cached.value as Data<GetPaginatedResponses<N>> & { status: 'loading' }, data)
        } catch (error) {
          if (isRateLimiterError(error as Error)) {
            logger.info(error)
            // Revert superseded task empty queue
            cached.value = notAsked({
              name,
              key,
              deduplicationKey,
              paramsHash: hash(params)
            })
          } else {
            logger.error(`Error fetching multiple ${name} with filter/params ${JSON.stringify(params)}:`, error)
            if (cached.value.status === 'loading') cached.value = setError(cached.value, error as Error)
          }
        }
      }

      // Invalidate cache if deduplicationKey has changed or caching is disabled
      if (deduplicationKey && cached.value.deduplicationKey !== deduplicationKey) {
        cached.value = notAsked({ name, key, deduplicationKey, paramsHash: hash(params) })
      }

      // Queue up a request if resource is new or stale or invalidated
      if (
        cached.value.status === 'notAsked'
        || cached.value.lastUpdated < Date.now() - config.value.maxAge
        || cached.value.paramsHash !== hash(params)
      ) {
        scheduleFetch()()
      }

      // Listen to external refetch signal
      // TODO: Replace with `activeObservers` reference-counting pattern https://dev.funkwhale.audio/funkwhale/funkwhale/-/issues/2645
      if (refetchSignal) {
        watch(refetchSignal, scheduleFetch())
      }

      // Add `key` and `refetch` for reactive UIs
      return computed(() => ({
        ...(searches.value[name].get(key) as Data<GetPaginatedResponses<N>>),
        key,
        refetch: scheduleFetch({ priority: true })
      }))
    }

  /**
   * @param name - the name of the resource, e.g. 'tracks' or 'albums'
   */
  const createBatchedResource = <N extends Name>(name: N) => {
    const resourceFactory = createResource(name)

    return memoize((
      params: Omit<Params<N>, 'page'>,
      options?: Parameters<typeof resourceFactory>[1]
    ) => {
      const pageRefs = ref([resourceFactory({ ...params, page: 1 }, options)])

      const watchNextPage = () => {
        const latestPageRef = pageRefs.value[pageRefs.value.length - 1]!
        const currentState = latestPageRef.value

        if (currentState.status === 'success') {
          if (currentState.data.next) {
            pageRefs.value.push(resourceFactory({ ...params, page: pageRefs.value.length + 1 }, options))
            watchNextPage()
          }
          return // Skip watcher creation
        }

        if (currentState.status === 'error') {
          return // Stop paginating on error
        }

        const unwatch = watch(
          latestPageRef,
          (state) => {
            if (state.status === 'success') {
              unwatch()
              if (state.data.next) {
                pageRefs.value.push(resourceFactory({ ...params, page: pageRefs.value.length + 1 }, options))
                watchNextPage()
              }
            } else if (state.status === 'error') {
              unwatch()
            }
          }
        )
      }

      watchNextPage()

      return computed(() =>
        pageRefs.value.slice(1).reduce((accumulated, { value: page }) =>
          page.status === 'error'
            // Preserve accumulated data
            ? ({ ...page, data: accumulated.data })
            : page.status === 'success'
              // Prepend accumulated results
              ? ({
                  ...accumulated, data: {
                    ...page.data, results: [
                      ...(accumulated.data?.results ?? []),
                      ...page.data.results
                    ]
                  }
                })
              // Use last loading/queued status
              : accumulated.status !== 'error' && (page.status === 'loading' || page.status === 'queued')
                ? ({ ...accumulated, status: page.status, lastUpdated: page.lastUpdated })
                : accumulated,
        pageRefs.value[0]!.value
        )
      )
    }, (params, options) => hash({ params, options /* Memoize-cache key */ }))
  }

  return {
    config,

    // Paginated resources
    'artists': createResource('artists'),
    'allArtists': createBatchedResource('artists'),
    'albums': createResource('albums'),
    'allAlbums': createBatchedResource('albums'),
    'tracks': createResource('tracks'),
    'allTracks': createBatchedResource('tracks'),
    'channels': createResource('channels'),
    'allChannels': createBatchedResource('channels'),
    'radios': createResource('radios/radios'),
    'allRadios': createBatchedResource('radios/radios'),
    'tags': createResource('tags'),
    'allTags': createBatchedResource('tags'),
    'playlists': createResource('playlists'),
    'allPlaylists': createBatchedResource('playlists'),
    'favorites/tracks': createResource('favorites/tracks'),
    'allFavoritesTracks': createBatchedResource('favorites/tracks'),
    'history/listenings': createResource('history/listenings'),
    'allHistoryListenings': createBatchedResource('history/listenings'),
    searches,

    // Other resources
    data,
    get,
    getMany,

    // Resource observers (global)
    'cachedResources': computed(() =>
      Object.values(searches.value).flatMap(entries => [...entries.values()])
    ),
    'latestResourcesByDeduplicationKey': (predicate: (deduplicationKey: string) => boolean) => computed(() =>
      Object.values(searches.value)
        .flatMap(entries => [...entries.values()])
        // Visible resources are the newest per deduplicationKey
        // TODO: Reimplement with `reduce` (or composed generator functions) to avoid duplicate iterations
        .filter((resource, _, allResources) => {
          if (typeof resource.deduplicationKey !== 'string') return false
          if (!predicate(resource.deduplicationKey)) return false
          const newerResourceWithSameKey = allResources.find(r =>
            r.deduplicationKey === resource.deduplicationKey
            && (r.lastRequested ?? 0) > (resource.lastRequested ?? 0)
          )
          return !newerResourceWithSameKey
        }
        )),
    'numberOfCachedResources': computed(() =>
      Object.values(searches.value).reduce((sum, entries) => sum + entries.size, 0)
    ),
    'numberOfQueuedResources': computed(() =>
      Object.values(searches.value).reduce((sum, entries) => sum + [...entries].filter(([_, v]) =>
        v.status === 'queued'
      ).length, 0)
    ),
    'numberOfLoadingResources': computed(() =>
      Object.values(searches.value).reduce((sum, entries) => sum + [...entries].filter(([_, v]) =>
        v.status === 'loading'
      ).length, 0)
    )
  }
})
